import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, Mail, FileText, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

interface InvestorReportingProps {
  companyId: string;
}

interface Report {
  id: string;
  period_start: string | null;
  period_end: string | null;
  report_url: string | null;
  sent_at: string | null;
  created_at: string;
}

export function InvestorReporting({ companyId }: InvestorReportingProps) {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from('investor_reports')
      .select('id, period_start, period_end, report_url, sent_at, created_at')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (error) {
      toast.error(error.message);
    } else {
      setReports(data || []);
    }
    setLoading(false);
  }

  async function generate() {
    setGenerating(true);
    toast('Generating investor report…');

    const { data, error } = await supabase.functions.invoke('generate-investor-report', {
      body: { companyId },
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Report generated successfully');
      load();
    }
    setGenerating(false);
  }

  async function send(id: string) {
    setSendingId(id);
    toast('Sending investor report to all shareholders…');

    const { data, error } = await supabase.functions.invoke('send-investor-report', {
      body: { reportId: id },
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success(`Report sent to ${data?.recipients || 0} investors`);
      load();
    }
    setSendingId(null);
  }

  useEffect(() => {
    load();
  }, [companyId]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold">Investor Reports</CardTitle>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-end">
          <Button onClick={generate} disabled={generating}>
            {generating ? (
              <Loader2 className="animate-spin h-4 w-4 mr-1" />
            ) : (
              <FileText className="h-4 w-4 mr-1" />
            )}
            Generate Report
          </Button>
        </div>

        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        ) : reports.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Report</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      {r.period_start && r.period_end
                        ? `${new Date(r.period_start).toLocaleDateString()} → ${new Date(r.period_end).toLocaleDateString()}`
                        : '—'}
                    </TableCell>
                    <TableCell>{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {r.sent_at ? (
                        new Date(r.sent_at).toLocaleDateString()
                      ) : (
                        <span className="text-muted-foreground">Not sent</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.report_url ? (
                        <a
                          href={r.report_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          View Report
                        </a>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => send(r.id)} disabled={sendingId === r.id}>
                        {sendingId === r.id ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <Mail className="h-4 w-4 mr-1" />
                        )}
                        Send
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm text-center py-8">
            No investor reports yet. Generate your first report to get started.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
